import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { getAllPermissions, getUserPermissions, updateUserPermissions } from '../../api/admin_api';

/**
 * Permission Assignment Modal
 * Allows admin to assign/revoke permissions for a user
 * @param {boolean} open - Modal open state
 * @param {function} onClose - Close handler
 * @param {object} user - User object { id, email, firstName, lastName }
 * @param {function} onSuccess - Callback after successful update
 */
const PermissionModal = ({ open, onClose, user, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allPermissions, setAllPermissions] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [error, setError] = useState('');

  // Fetch permissions on mount
  useEffect(() => {
    console.log('=== PermissionModal useEffect ===');
    console.log('Modal open:', open);
    console.log('User object:', user);
    console.log('User ID:', user?.id);
    console.log('User email:', user?.email);
    
    if (open && user && user.id) {
      console.log('Fetching permissions for user ID:', user.id);
      fetchPermissions();
    } else {
      console.log('Not fetching permissions - open:', open, 'user?.id:', user?.id);
    }
  }, [open, user?.id]);

  // Add safety checks for props after all hooks
  if (!onClose || typeof onClose !== 'function') {
    console.error('PermissionModal: onClose prop is missing or not a function');
    return null;
  }
  
  if (!onSuccess || typeof onSuccess !== 'function') {
    console.error('PermissionModal: onSuccess prop is missing or not a function');
    return null;
  }

  const fetchPermissions = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Fetching permissions for user:', user.id);
      
      // First, get all available permissions
      let allPermissionsData = [];
      try {
        console.log('Fetching all permissions from API...');
        const permissionsResponse = await getAllPermissions();
        console.log('Permissions API response:', permissionsResponse);
        console.log('Permissions response type:', typeof permissionsResponse);
        console.log('Permissions response data:', permissionsResponse?.data);
        console.log('Permissions response data type:', typeof permissionsResponse?.data);
        console.log('Permissions response data is array:', Array.isArray(permissionsResponse?.data));
        
        if (permissionsResponse && permissionsResponse.data) {
          if (Array.isArray(permissionsResponse.data)) {
            allPermissionsData = permissionsResponse.data;
            console.log('All permissions loaded from API:', allPermissionsData.length);
          } else {
            console.error('API returned non-array data:', permissionsResponse.data);
            throw new Error('API returned invalid data format');
          }
        }
      } catch (apiError) {
        console.log('Failed to fetch all permissions from API, using mock data:', apiError);
        // Use mock permissions as fallback
        allPermissionsData = [
        { permissionId: 'Settings', permissionKey: 'Settings', permissionName: 'Settings', category: 'Settings', description: 'Main settings access' },
        { permissionId: 'Settings.LPManagement', permissionKey: 'Settings.LPManagement', permissionName: 'LP Management', category: 'Settings', description: 'Liquidity Provider Management' },
        { permissionId: 'Settings.Security', permissionKey: 'Settings.Security', permissionName: 'Security', category: 'Settings', description: 'Security Settings' },
        { permissionId: 'Settings.DataManagement', permissionKey: 'Settings.DataManagement', permissionName: 'Data Management', category: 'Settings', description: 'Data Management Settings' },
        { permissionId: 'Settings.NotificationAppearance', permissionKey: 'Settings.NotificationAppearance', permissionName: 'Notification & Appearance', category: 'Settings', description: 'Notification and Appearance Settings' },
        { permissionId: 'Settings.Strategies', permissionKey: 'Settings.Strategies', permissionName: 'Strategies', category: 'Settings', description: 'Trading Strategies' },
        { permissionId: 'Settings.LPManagement.Symbols', permissionKey: 'Settings.LPManagement.Symbols', permissionName: 'LP Symbols', category: 'Settings', description: 'LP Symbol Management' },
        { permissionId: 'Settings.LPManagement.EditLP', permissionKey: 'Settings.LPManagement.EditLP', permissionName: 'Edit LP', category: 'Settings', description: 'Edit Liquidity Provider' },
        { permissionId: 'Settings.LPManagement.LPConfiguration', permissionKey: 'Settings.LPManagement.LPConfiguration', permissionName: 'LP Configuration', category: 'Settings', description: 'LP Configuration Management' },
        { permissionId: 'Settings.LPManagement.ConnectionSetting', permissionKey: 'Settings.LPManagement.ConnectionSetting', permissionName: 'Connection Settings', category: 'Settings', description: 'LP Connection Settings' },
        { permissionId: 'Settings.LPManagement.DeleteLP', permissionKey: 'Settings.LPManagement.DeleteLP', permissionName: 'Delete LP', category: 'Settings', description: 'Delete Liquidity Provider' },
        { permissionId: 'Settings.LPManagement.Symbols.View', permissionKey: 'Settings.LPManagement.Symbols.View', permissionName: 'View LP Symbols', category: 'Settings', description: 'View LP Symbols' },
        { permissionId: 'Settings.LPManagement.Symbols.Edit', permissionKey: 'Settings.LPManagement.Symbols.Edit', permissionName: 'Edit LP Symbols', category: 'Settings', description: 'Edit LP Symbols' },
        { permissionId: 'Settings.LPManagement.Symbols.Create', permissionKey: 'Settings.LPManagement.Symbols.Create', permissionName: 'Create LP Symbols', category: 'Settings', description: 'Create LP Symbols' },
        { permissionId: 'Settings.LPManagement.Symbols.Delete', permissionKey: 'Settings.LPManagement.Symbols.Delete', permissionName: 'Delete LP Symbols', category: 'Settings', description: 'Delete LP Symbols' },
        { permissionId: 'Settings.LPManagement.LPConfiguration.View', permissionKey: 'Settings.LPManagement.LPConfiguration.View', permissionName: 'View LP Configuration', category: 'Settings', description: 'View LP Configuration' },
        { permissionId: 'Settings.LPManagement.LPConfiguration.Edit', permissionKey: 'Settings.LPManagement.LPConfiguration.Edit', permissionName: 'Edit LP Configuration', category: 'Settings', description: 'Edit LP Configuration' },
        { permissionId: 'Settings.LPManagement.LPConfiguration.Create', permissionKey: 'Settings.LPManagement.LPConfiguration.Create', permissionName: 'Create LP Configuration', category: 'Settings', description: 'Create LP Configuration' },
        { permissionId: 'Settings.LPManagement.LPConfiguration.Delete', permissionKey: 'Settings.LPManagement.LPConfiguration.Delete', permissionName: 'Delete LP Configuration', category: 'Settings', description: 'Delete LP Configuration' },
        { permissionId: 'Settings.LPManagement.EditLP.Edit', permissionKey: 'Settings.LPManagement.EditLP.Edit', permissionName: 'Edit LP Details', category: 'Settings', description: 'Edit LP Details' },
        { permissionId: 'Settings.LPManagement.EditLP.View', permissionKey: 'Settings.LPManagement.EditLP.View', permissionName: 'View LP Details', category: 'Settings', description: 'View LP Details' },
        { permissionId: 'Settings.LPManagement.EditLP.Delete', permissionKey: 'Settings.LPManagement.EditLP.Delete', permissionName: 'Delete LP Details', category: 'Settings', description: 'Delete LP Details' },
        { permissionId: 'Settings.LPManagement.ConnectionSetting.Edit', permissionKey: 'Settings.LPManagement.ConnectionSetting.Edit', permissionName: 'Edit Connection Settings', category: 'Settings', description: 'Edit Connection Settings' },
        { permissionId: 'Settings.LPManagement.ConnectionSetting.View', permissionKey: 'Settings.LPManagement.ConnectionSetting.View', permissionName: 'View Connection Settings', category: 'Settings', description: 'View Connection Settings' },
        { permissionId: 'Settings.DataManagement.View', permissionKey: 'Settings.DataManagement.View', permissionName: 'View Data Management', category: 'Settings', description: 'View Data Management' },
        { permissionId: 'Settings.DataManagement.Edit', permissionKey: 'Settings.DataManagement.Edit', permissionName: 'Edit Data Management', category: 'Settings', description: 'Edit Data Management' },
        { permissionId: 'Settings.DataManagement.Delete', permissionKey: 'Settings.DataManagement.Delete', permissionName: 'Delete Data Management', category: 'Settings', description: 'Delete Data Management' },
        { permissionId: 'Settings.DataManagement.Create', permissionKey: 'Settings.DataManagement.Create', permissionName: 'Create Data Management', category: 'Settings', description: 'Create Data Management' },
        { permissionId: 'Settings.Strategies.View', permissionKey: 'Settings.Strategies.View', permissionName: 'View Strategies', category: 'Settings', description: 'View Trading Strategies' },
        { permissionId: 'Settings.Strategies.Delete', permissionKey: 'Settings.Strategies.Delete', permissionName: 'Delete Strategies', category: 'Settings', description: 'Delete Trading Strategies' },
        { permissionId: 'Settings.Strategies.Edit', permissionKey: 'Settings.Strategies.Edit', permissionName: 'Edit Strategies', category: 'Settings', description: 'Edit Trading Strategies' },
        { permissionId: 'Settings.Strategies.Create', permissionKey: 'Settings.Strategies.Create', permissionName: 'Create Strategies', category: 'Settings', description: 'Create Trading Strategies' },
        { permissionId: 'Dashboard.MarketWatchSection', permissionKey: 'Dashboard.MarketWatchSection', permissionName: 'Market Watch Section', category: 'Dashboard', description: 'Access to Market Watch Section' },
        { permissionId: 'Dashboard.MonitoringSection', permissionKey: 'Dashboard.MonitoringSection', permissionName: 'Monitoring Section', category: 'Dashboard', description: 'Access to Monitoring Section' },
        { permissionId: 'Dashboard.TradePanel', permissionKey: 'Dashboard.TradePanel', permissionName: 'Trade Panel', category: 'Dashboard', description: 'Access to Trade Panel' },
        { permissionId: 'Dashboard.TradePanel.FixLogs', permissionKey: 'Dashboard.TradePanel.FixLogs', permissionName: 'FIX Logs', category: 'Dashboard', description: 'Access to FIX Logs' },
        { permissionId: 'Dashboard.TradePanel.TradeLogs', permissionKey: 'Dashboard.TradePanel.TradeLogs', permissionName: 'Trade Logs', category: 'Dashboard', description: 'Access to Trade Logs' },
        { permissionId: 'Dashboard.QuickOrder', permissionKey: 'Dashboard.QuickOrder', permissionName: 'Quick Order', category: 'Dashboard', description: 'Access to Quick Order' },
        { permissionId: 'Dashboard.SpreadOrder', permissionKey: 'Dashboard.SpreadOrder', permissionName: 'Spread Order', category: 'Dashboard', description: 'Access to Spread Order' }
      ];
      }
      
      // Group permissions by category
      console.log('All permissions data type:', typeof allPermissionsData);
      console.log('All permissions data is array:', Array.isArray(allPermissionsData));
      console.log('All permissions data length:', allPermissionsData?.length);
      
      if (Array.isArray(allPermissionsData) && allPermissionsData.length > 0) {
        const grouped = groupPermissionsByCategory(allPermissionsData);
      setAllPermissions(grouped);
        console.log('Permissions grouped successfully:', Object.keys(grouped));
      } else {
        console.error('Invalid permissions data:', allPermissionsData);
        setError('Failed to load permissions data');
        return;
      }
      
      // Now fetch the user's current permissions
      let userCurrentPermissions = [];
      try {
        console.log('=== Fetching User Permissions ===');
        console.log('User ID being sent to API:', user.id);
        console.log('User ID type:', typeof user.id);
        console.log('User object in modal:', user);
        console.log('User permissions from user object:', user.permissions);
        console.log('Fetching user permissions from API...');
        const userPermsResponse = await getUserPermissions(user.id);
        console.log('User permissions API response:', userPermsResponse);
        
        // Handle different response formats
        if (userPermsResponse) {
          console.log('Raw API response:', userPermsResponse);
          
          // Handle direct array response (current API format)
          if (Array.isArray(userPermsResponse)) {
            console.log('Received direct array response for user permissions');
            console.log('Number of permissions returned:', userPermsResponse.length);
            
            // Check if permissions have isGranted field
            const hasIsGrantedField = userPermsResponse.some(p => p.hasOwnProperty('isGranted'));
            console.log('Has isGranted field:', hasIsGrantedField);
            
            if (hasIsGrantedField) {
              // API returns permissions with isGranted flag
              userCurrentPermissions = userPermsResponse
                .filter(p => p.isGranted === true)
                .map(p => p.permissionKey || p.permissionId);
              console.log('Filtered granted permissions:', userCurrentPermissions);
            } else {
              // API returns user's permissions directly (without isGranted field)
              userCurrentPermissions = userPermsResponse.map(p => p.permissionKey || p.permissionId);
              console.log('Direct user permissions:', userCurrentPermissions);
            }
          }
          // Handle wrapped response format
          else if (userPermsResponse.success && userPermsResponse.data) {
            console.log('Received wrapped response format for user permissions');
            console.log('Number of permissions returned:', userPermsResponse.data.length);
            
            const hasIsGrantedField = userPermsResponse.data.some(p => p.hasOwnProperty('isGranted'));
            console.log('Has isGranted field:', hasIsGrantedField);
            
            if (hasIsGrantedField) {
              userCurrentPermissions = userPermsResponse.data
                .filter(p => p.isGranted === true)
                .map(p => p.permissionKey || p.permissionId);
              console.log('Filtered granted permissions:', userCurrentPermissions);
            } else {
              userCurrentPermissions = userPermsResponse.data.map(p => p.permissionKey || p.permissionId);
              console.log('Direct user permissions:', userCurrentPermissions);
            }
          }
          // Handle other response formats
          else {
            console.log('Unknown response format for user permissions:', userPermsResponse);
            userCurrentPermissions = [];
          }
          
          console.log('Final user current permissions:', userCurrentPermissions);
        }
      } catch (userPermsError) {
        console.log('Failed to fetch user permissions from API:', userPermsError);
        console.log('Trying to extract permissions from user object...');
        
        // Fallback: try to get permissions from user object if available
        if (user && user.permissions && Array.isArray(user.permissions)) {
          console.log('Found permissions in user object:', user.permissions);
          userCurrentPermissions = user.permissions
            .filter(p => p.isGranted === true)
            .map(p => p.permissionKey || p.permissionId);
          console.log('Extracted permissions from user object:', userCurrentPermissions);
        } else {
          console.log('No permissions found in user object, starting with empty permissions');
          userCurrentPermissions = [];
        }
      }
      
      // Set the user's current permissions
      setSelectedPermissions(userCurrentPermissions);
      console.log('Final selected permissions:', userCurrentPermissions);
      
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to load permissions. Please try again.');
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const groupPermissionsByCategory = (permissions) => {
    console.log('groupPermissionsByCategory called with:', permissions);
    console.log('Permissions type:', typeof permissions);
    console.log('Permissions is array:', Array.isArray(permissions));
    
    if (!Array.isArray(permissions)) {
      console.error('groupPermissionsByCategory: permissions is not an array:', permissions);
      return {};
    }
    
    const grouped = {};
    permissions.forEach(permission => {
      if (!grouped[permission.category]) {
        grouped[permission.category] = [];
      }
      grouped[permission.category].push(permission);
    });
    return grouped;
  };

  const handlePermissionToggle = (permission) => {
    console.log('=== PERMISSION TOGGLE DEBUG ===');
    console.log('Permission object:', permission);
    console.log('Permission ID:', permission.permissionId);
    console.log('Permission Key:', permission.permissionKey);
    console.log('Current selected permissions:', selectedPermissions);
    
    // Use permissionKey as the primary identifier for consistency
    const permissionKey = permission.permissionKey || permission.permissionId;
    
    setSelectedPermissions(prev => {
      const isCurrentlySelected = prev.includes(permissionKey);
      console.log('Is currently selected:', isCurrentlySelected);
      
      if (isCurrentlySelected) {
        const newSelection = prev.filter(id => id !== permissionKey);
        console.log('Removing permission, new selection:', newSelection);
        return newSelection;
      } else {
        const newSelection = [...prev, permissionKey];
        console.log('Adding permission, new selection:', newSelection);
        return newSelection;
      }
    });
  };

  const handleSelectAll = () => {
    const allPermissionKeys = Object.values(allPermissions)
      .flat()
      .map(p => p.permissionKey || p.permissionId);
    console.log('Selecting all permissions:', allPermissionKeys);
    setSelectedPermissions(allPermissionKeys);
  };

  const handleDeselectAll = () => {
    setSelectedPermissions([]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      console.log('=== PERMISSION UPDATE DEBUG ===');
      console.log('User ID:', user?.id);
      console.log('Selected permissions:', selectedPermissions);
      console.log('Permission count:', selectedPermissions.length);
      
      // Validate that we have permissions to save
      if (!selectedPermissions || selectedPermissions.length === 0) {
        throw new Error('No permissions selected');
      }
      
      // Convert permission keys to actual permission IDs from the API response
      const permissionIds = selectedPermissions.map(permissionKey => {
        // Find the permission object to get the actual permissionId
        const permission = Object.values(allPermissions)
          .flat()
          .find(p => (p.permissionKey || p.permissionId) === permissionKey);
        
        console.log('Looking for permission key:', permissionKey);
        console.log('Found permission object:', permission);
        console.log('Permission ID from object:', permission?.permissionId);
        
        return permission?.permissionId || permission?.id;
      }).filter(id => id !== undefined);
      
      console.log('Converted permission IDs:', permissionIds);
      
      // Call the real API to update permissions
      console.log('Calling updateUserPermissions API...');
      const response = await updateUserPermissions(user.id, permissionIds);
      
      console.log('API Response:', response);
      
      if (response && response.success) {
        console.log('Permissions updated successfully!');
        toast.success('Permissions updated successfully!');
        onSuccess && onSuccess();
        onClose();
      } else {
        console.error('API returned failure:', response);
        throw new Error(response?.error || response?.message || 'Failed to update permissions');
      }
    } catch (err) {
      console.error('=== PERMISSION UPDATE ERROR ===');
      console.error('Error details:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response?.data);
      
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update permissions. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Settings': '📋',
      'Dashboard': '📊',
      'TradePanel': '💼',
      'DataManagement': '💾',
      'Security': '🔒',
      'Notifications': '🔔',
      'Appearance': '🎨'
    };
    return icons[category] || '🔐';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Settings': '#BB897F',
      'Dashboard': '#6B9BD1',
      'TradePanel': '#27B56D',
      'DataManagement': '#FF6B35',
      'Security': '#E74C3C',
      'Notifications': '#F39C12',
      'Appearance': '#9B59B6'
    };
    return colors[category] || '#757575';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(41, 43, 47, 1)',
          color: 'rgba(255, 255, 255, 1)',
          borderRadius: 3,
          boxShadow: '0 25px 50px 0 rgba(0, 0, 0, 0.35)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
      }}>
        <Box>
          <Typography variant="h6" component="div" sx={{ color: 'rgba(255, 255, 255, 1)', fontWeight: 600 }}>
            Manage Permissions
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 0.5 }}>
            Assign permissions to: {user?.email} ({user?.firstName} {user?.lastName})
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{
        borderColor: 'rgba(255, 255, 255, 0.1)',
        backgroundColor: 'rgba(41, 43, 47, 1)',
        pt: 3
      }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress sx={{ color: '#7C3AED' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Bulk Actions */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleSelectAll}
                sx={{
                  color: '#7c3aed',
                  borderColor: '#7C3AED',
                  '&:hover': {
                    borderColor: '#9F7AEA',
                    backgroundColor: 'rgba(124, 58, 237, 0.1)'
                  }
                }}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDeselectAll}
                sx={{
                  color: 'rgba(171, 171, 171, 1)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Deselect All
              </Button>
              <Box sx={{ ml: 'auto', color: 'rgba(171, 171, 171, 1)' }}>
                <Typography variant="body2">
                  {selectedPermissions.length} permissions selected
                </Typography>
              </Box>
            </Box>

            {/* Permission Categories */}
            {Object.keys(allPermissions).map((category, index) => {
              const categoryPerms = allPermissions[category];
              const selectedInCategory = categoryPerms.filter(p => {
                const permissionKey = p.permissionKey || p.permissionId;
                return selectedPermissions.includes(permissionKey);
              }).length;

              return (
                <Box key={category} sx={{ mb: 3 }}>
                  {index > 0 && <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />}

                  {/* Category Header */}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>
                      {getCategoryIcon(category)} {category.toUpperCase()} PERMISSIONS
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: getCategoryColor(category),
                        fontWeight: 500
                      }}
                    >
                      ({selectedInCategory} of {categoryPerms.length} selected)
                    </Typography>
                  </Box>

                  {/* Permission Checkboxes */}
                  <FormGroup>
                    {categoryPerms.map((permission) => (
                      <Box
                        key={permission.permissionId}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: selectedPermissions.includes(permission.permissionKey || permission.permissionId)
                            ? 'rgba(124, 58, 237, 0.15)'
                            : 'rgba(51, 53, 57, 1)',
                          border: '1px solid',
                          borderColor: selectedPermissions.includes(permission.permissionKey || permission.permissionId)
                            ? '#7C3AED'
                            : 'rgba(255, 255, 255, 0.1)',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(124, 58, 237, 0.1)',
                            borderColor: '#7C3AED'
                          }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(permission.permissionKey || permission.permissionId)}
                              onChange={() => handlePermissionToggle(permission)}
                              sx={{
                                color: 'rgba(171, 171, 171, 1)',
                                '&.Mui-checked': {
                                  color: '#7C3AED'
                                }
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: 'rgba(255, 255, 255, 1)' }}>
                                {permission.permissionName}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(171, 171, 171, 1)', fontSize: '0.85rem' }}>
                                {permission.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    ))}
                  </FormGroup>
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: 2.5,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: 'rgba(51, 53, 57, 1)'
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={saving}
          sx={{
            color: 'rgba(171, 171, 171, 1)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
          sx={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
            color: '#fff',
            minWidth: 200,
            px: 3,
            '&:hover': {
              background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)'
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
              Saving...
            </>
          ) : (
            `Save Permissions (${selectedPermissions.length} selected)`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionModal;
